import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  useNotification,
  getDialogHeading,
  dropDownValidation,
} from 'utils/commonUtils';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import TutorDialogCard from './TutorDialogCard';
import Typography from '@mui/material/Typography';
import { yupResolver } from '@hookform/resolvers/yup';
import { SpinnerWrapper } from '../../tutoringV2Styled';
import DialogContent from '@mui/material/DialogContent';
import {
  removeAssignedTutor,
  getTutoringSubjectsV2Tutors,
  postTutoringSubjectsV2Tutor,
  getTutoringSubjectsV2TimeSlots,
  putTutoringSubjectV2TutorAssignment,
} from 'layouts/tutoringV2/services/tutoringAndSubjectsV2Api';
import { TimeSlotAccordionPicker } from './TimeSlotsAccordion';
import { TutorsDialogLoader } from '../../tutoringV2CommonUtils';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { ButtonSpinner, ButtonSpinnerProgress } from 'components/Ui/styled';
import { DialogAutoHeightStyle, manageTutorCategoryOptions } from 'constants';

const subjectsAndTurorsSchema = yup.object().shape({
  tutor: dropDownValidation,
  category: dropDownValidation,
});

const ManageTutorsDialog = (props) => {
  const { open, setOpen, selectedSubject, onTutorsUpdated } = props;
  const { subjectId } = selectedSubject;

  const { handleErrorResponse, NotificationPopup } = useNotification();

  const availableUsersList =
    useSelector((state) => state?.availableUsersList) || [];

  const [timeSlots, setTimeSlots] = useState([]);
  const [tutorsList, setTutorsList] = useState([]);
  const [editingSlots, setEditingSlots] = useState({});
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [tutorsLoading, setTutorsLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);
  const [removingTutorId, setRemovingTutorId] = useState(null);
  const [switchingTutorId, setSwitchingTutorId] = useState(null);
  const [updatingSlotsTutorId, setUpdatingSlotsTutorId] = useState(null);

  const availableUsersData =
    availableUsersList?.map((item) => ({
      id: item?.userId,
      value: `${item?.givenName} ${item?.familyName} (${item?.username})`,
    })) || [];

  const defaultValue = {
    tutor: availableUsersData?.[0]?.id || '',
    category: manageTutorCategoryOptions?.[0]?.id || '',
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(subjectsAndTurorsSchema),
  });

  const { handleSubmit, reset, control, formState } = useFunction;
  const { errors } = formState;

  useEffect(() => {
    reset(defaultValue);
  }, [defaultValue.tutor, defaultValue.category]);

  const loadSlots = async () => {
    setSlotsLoading(true);
    const res = await getTutoringSubjectsV2TimeSlots(subjectId);
    setTimeSlots(res?.timeSlots || []);
    try {
      const list = await getTutoringSubjectsV2Tutors(subjectId);
      setTutorsList(list || []);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    } finally {
      setSlotsLoading(false);
    }
  };

  const loadTutors = async (showLoader = false) => {
    try {
      if (showLoader) {
        setTutorsLoading(true);
      }

      const list = await getTutoringSubjectsV2Tutors(subjectId);
      const fetched = list || [];
      setTutorsList(fetched);

      const seed = {};
      fetched.forEach((t) => {
        seed[t.assignmentId] = t.allowedTimeSlotIds || [];
      });
      setEditingSlots(seed);
    } finally {
      if (showLoader) {
        setTutorsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadSlots();
    loadTutors(true);
  }, [subjectId]);

  const handleClose = () => {
    setOpen(false);
  };

  /* ---------------- add tutor ---------------- */
  const onSubmit = async (data) => {
    const { category, tutor } = data;
    const payload = {
      category,
      tutorId: tutor,
      allowedTimeSlotIds: selectedSlotIds.length ? selectedSlotIds : null,
    };

    try {
      setLoadingButton(true);
      await postTutoringSubjectsV2Tutor(subjectId, payload);
      setSelectedSlotIds([]);
      await loadTutors();
      onTutorsUpdated?.();
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    } finally {
      setLoadingButton(false);
    }
  };

  const getTutorName = (tutorId) => {
    const user = availableUsersList?.find((u) => u.userId === tutorId);
    return user ? `${user.givenName} ${user.familyName}` : tutorId;
  };

  const handleSwitchCategory = async (tutor) => {
    const { assignmentId, category } = tutor;
    const newCategory = category === 'primary' ? 'backup' : 'primary';

    const slotIds = editingSlots[assignmentId] || [];

    const payload = {
      category: newCategory,
      allowedTimeSlotIds: slotIds,
    };

    try {
      setSwitchingTutorId(assignmentId);
      await putTutoringSubjectV2TutorAssignment(assignmentId, payload);
      await loadTutors();
      onTutorsUpdated?.();
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    } finally {
      setSwitchingTutorId(null);
    }
  };

  const handleUpdateTimeSlots = async (tutor) => {
    const { assignmentId, category } = tutor;
    const slotIds = editingSlots[assignmentId] || [];

    const payload = {
      category,
      allowedTimeSlotIds: slotIds,
    };

    try {
      setUpdatingSlotsTutorId(assignmentId);
      await putTutoringSubjectV2TutorAssignment(assignmentId, payload);
      await loadTutors();
      onTutorsUpdated?.();
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    } finally {
      setUpdatingSlotsTutorId(null);
    }
  };

  const handleRemoveTutor = async (tutor) => {
    const { assignmentId } = tutor;

    try {
      setRemovingTutorId(assignmentId);
      await removeAssignedTutor(assignmentId);
      await loadTutors();
      onTutorsUpdated?.();
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    } finally {
      setRemovingTutorId(null);
    }
  };

  const showTutors = !tutorsLoading && tutorsList?.length > 0;

  const handleCardSlotsChange = (assignmentId, ids) => {
    setEditingSlots((prev) => ({ ...prev, [assignmentId]: ids }));
  };

  return (
    <Dialog open={open} PaperProps={DialogAutoHeightStyle}>
      {getDialogHeading('Manage Tutors', handleClose)}
      <DialogContent>
        {/* ---------- Add Tutor ---------- */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ButtonSpinner>
                <SpinnerWrapper width="100%">
                  <TimeSlotAccordionPicker
                    timeSlots={timeSlots}
                    selectedIds={selectedSlotIds}
                    onChange={setSelectedSlotIds}
                  />
                  {slotsLoading && <ButtonSpinnerProgress size={24} />}
                </SpinnerWrapper>
              </ButtonSpinner>
            </Grid>

            <Grid item xs={12} md={7}>
              <FormInputDropdown
                type="text"
                name="tutor"
                control={control}
                label="Tutor Name"
                errors={!!errors.tutor}
                options={availableUsersData}
                helperText={errors?.tutor?.message}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <FormInputDropdown
                type="text"
                name="category"
                label="Category"
                control={control}
                errors={!!errors.category}
                options={manageTutorCategoryOptions}
                helperText={errors?.category?.message}
              />
            </Grid>

            <Grid item xs={12} md={2.5} alignContent="end">
              <ButtonSpinner>
                <SpinnerWrapper>
                  <Button
                    disabled={loadingButton}
                    onClick={handleSubmit(onSubmit)}
                  >
                    {loadingButton ? 'Adding...' : '+ Add Tutor'}
                  </Button>
                  {loadingButton && <ButtonSpinnerProgress size={24} />}
                </SpinnerWrapper>
              </ButtonSpinner>
            </Grid>
          </Grid>
        </Paper>

        {/* ---------- Tutor Cards ---------- */}
        <Grid container spacing={2} mt={2}>
          {tutorsLoading && <TutorsDialogLoader />}

          {!tutorsLoading && tutorsList?.length === 0 && (
            <Grid item xs={12}>
              <Typography align="center">No tutors assigned.</Typography>
            </Grid>
          )}

          {showTutors &&
            tutorsList?.map((tutor) => {
              const isLoading = removingTutorId === tutor?.assignmentId;
              const isSwitching = switchingTutorId === tutor?.assignmentId;
              const isUpdatingSlots =
                updatingSlotsTutorId === tutor.assignmentId;
              return (
                <TutorDialogCard
                  key={tutor.assignmentId}
                  tutor={tutor}
                  loading={isLoading}
                  timeSlots={timeSlots}
                  isSwitching={isSwitching}
                  getTutorName={getTutorName}
                  slotsLoading={slotsLoading}
                  onRemoveTutor={handleRemoveTutor}
                  isUpdatingSlots={isUpdatingSlots}
                  onSwitchCategory={handleSwitchCategory}
                  onUpdateTimeSlots={handleUpdateTimeSlots}
                  onCardSlotsChange={(ids) =>
                    handleCardSlotsChange(tutor?.assignmentId, ids)
                  }
                  cardSlotIds={editingSlots[tutor?.assignmentId] ?? []}
                />
              );
            })}
        </Grid>
      </DialogContent>
      <NotificationPopup />
    </Dialog>
  );
};

export default ManageTutorsDialog;
